import { useState } from 'react';
import type { PublicationDossier } from '@youkuai/contracts';
import { PUBLICATION_VIDEO_MAX_LABEL } from '@youkuai/contracts';

import { Icon } from './icons.tsx';

function formatTime(value: string | null): string {
  if (!value) return '待定';
  return new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value));
}

function attachmentLabel(attachment: PublicationDossier['attachments'][number]): string {
  const version = attachment.versionNo === null ? '当前文件' : `V${attachment.versionNo}`;
  return `${version} · ${attachment.uploadedBy ?? '未知上传人'} · ${formatTime(attachment.uploadedAt)}`;
}

/**
 * BUG-0177 阶段一：右侧发布执行单抽屉的只读五区块。
 * 字段口径见 docs/bug-0177-phase1-contract.md；空值必须显式说明，来源必须分开标注。
 */
export function PublicationDossierBlocks({ dossier, videoBusy = false, onUploadVideos, onDownloadVideo,
  onDeleteVideo }: { dossier: PublicationDossier; videoBusy?: boolean;
  onUploadVideos?: ((files: FileList | null) => void) | undefined;
  onDownloadVideo?: ((videoId: string) => void) | undefined;
  onDeleteVideo?: ((videoId: string) => void) | undefined }) {
  const [copyExpanded, setCopyExpanded] = useState(false);
  const shared = dossier.attachments.filter((item) => item.source === 'SHARED_ATTACHMENT');
  const materials = dossier.attachments.filter((item) => item.source === 'MATERIAL');
  const nearest = dossier.plannedTimes.filter((item) => item.plannedAt !== null)
    .sort((left, right) => (left.plannedAt! < right.plannedAt! ? -1 : 1))[0] ?? null;
  return <div className="publication-dossier-blocks">
    <section className="dossier-block" data-dossier-block="title">
      <h3><span>①</span>发布标题</h3>
      {dossier.defaultTitle ? <p className="dossier-title"><b>{dossier.defaultTitle}</b><small>内容级默认标题</small></p>
        : <p className="dossier-empty">未填写发布标题</p>}
      {dossier.platformTitles.length > 0 && <ul className="dossier-platform-titles">
        {dossier.platformTitles.map((title) => <li key={title.platform}><small>{title.platform}</small>
          <span>{title.title || '未填写发布标题'}</span></li>)}
      </ul>}
    </section>
    <section className="dossier-block" data-dossier-block="description">
      <h3><span>②</span>发布描述</h3>
      {dossier.description ? <>
        <p className={copyExpanded ? 'dossier-copy is-expanded' : 'dossier-copy'}>{dossier.description}</p>
        <div className="dossier-copy-actions">
          <button type="button" onClick={() => setCopyExpanded(!copyExpanded)}>
            {copyExpanded ? '收起' : `展开全文（${dossier.description.length} 字）`}</button>
          <button type="button" onClick={() => void navigator.clipboard?.writeText(dossier.description ?? '')}>复制</button>
        </div></> : <p className="dossier-empty">策划未填写发布描述</p>}
    </section>
    {dossier.tags.length > 0 && <section className="dossier-block" data-dossier-block="tags">
      <h3><span>③</span>标签</h3>
      <div className="dossier-tags">{dossier.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>
    </section>}
    <section className="dossier-block" data-dossier-block="attachments">
      <h3><span>④</span>附件<small>{dossier.attachments.length ? `${dossier.attachments.length} 项 · 复用材料正式版与共享附件` : ''}</small></h3>
      {dossier.attachments.length === 0 && <p className="dossier-empty">没有可下载的附件</p>}
      {shared.length > 0 && <div className="dossier-attachment-group"><h4>内容共享附件</h4>
        {shared.map((attachment) => <article key={`shared-${attachment.id}`}>
          <span><b>{attachment.title}</b><small>{attachment.kind === 'LINK' ? '外部链接' : attachment.filename ?? attachment.title} · {attachmentLabel(attachment)}</small></span>
          {attachment.downloadPath && <a href={attachment.downloadPath}>下载 <Icon name="arrowRight" size={12} /></a>}
        </article>)}</div>}
      {materials.length > 0 && <div className="dossier-attachment-group"><h4>材料与版本正式版</h4>
        {materials.map((attachment) => <article key={`material-${attachment.id}`}>
          <span><b>{attachment.title}</b><small>{attachment.taskCode ? `${attachment.taskCode} · ` : ''}
            {attachment.kind === 'FILE' ? attachment.filename ?? '附件文件' : attachment.kind === 'MARKDOWN' ? 'Markdown 正式版' : '外部链接'}
            {' · '}{attachmentLabel(attachment)}</small></span>
          {attachment.downloadPath && <a href={attachment.downloadPath}>下载 <Icon name="arrowRight" size={12} /></a>}
        </article>)}</div>}
    </section>
    <section className="dossier-block" data-dossier-block="planned-times">
      <h3><span>⑤</span>预期发布时间{nearest && <small>最近：{nearest.platform} {formatTime(nearest.plannedAt)}</small>}</h3>
      {dossier.plannedTimes.length === 0 ? <p className="dossier-empty">尚未设定预期发布时间</p> :
        <ul className="dossier-planned-times">{dossier.plannedTimes.map((item) => <li key={item.platform}
          className={nearest && item.platform === nearest.platform ? 'is-nearest' : ''}>
          <small>{item.platform}</small><span>{formatTime(item.plannedAt)}</span></li>)}</ul>}
    </section>
    <section className="dossier-block" data-dossier-block="videos">
      <h3><span>▣</span>成片文件<small>{dossier.videos.length ? `${dossier.videos.length} 个` : ''}</small></h3>
      {dossier.videos.length === 0 ? <p className="dossier-empty">还没有成片文件</p> :
        <ul className="dossier-videos">{dossier.videos.map((video) => <li key={video.videoId}>
          <b>{video.originalFilename}</b>
          <small>{Math.max(1, Math.round(video.byteSize / 1024))} KB · {video.uploadedBy ?? '未知上传人'} · {formatTime(video.uploadedAt)}</small>
          <span className="dossier-video-actions">
            {onDownloadVideo && <button type="button" disabled={videoBusy}
              onClick={() => onDownloadVideo(video.videoId)}>下载</button>}
            {onDeleteVideo && <button type="button" disabled={videoBusy}
              onClick={() => onDeleteVideo(video.videoId)}>删除</button>}
          </span>
        </li>)}</ul>}
      {onUploadVideos && <label className="dossier-upload">
        <input type="file" multiple disabled={videoBusy}
          onChange={(event) => { onUploadVideos(event.target.files); event.target.value = ''; }} />
        <span>{videoBusy ? '处理中…' : `上传成片（≤${PUBLICATION_VIDEO_MAX_LABEL}，不限格式）`}</span>
      </label>}
    </section>
  </div>;
}
